import { permanentRedirect } from "next/navigation";
import { langPath, type Lang } from "@/lib/i18n";

/**
 * If the slug in the URL doesn't match the canonical slug from the API,
 * issue a permanent (308) redirect to the canonical URL. Uses
 * `permanentRedirect` (308) rather than `redirect` (307 temporary) so search
 * engines collapse stale-slug URLs onto the canonical one instead of treating
 * them as a transient detour.
 */
export function canonicalizeSlug(opts: {
  lang: Lang;
  type: "courses" | "events" | "otts" | "podcasts" | "authors" | "organizations";
  id: string | number;
  paramSlug: string;
  canonicalSlug: string | null | undefined;
  /** Canonicalize within the in-app /library surface instead of the public one. */
  library?: boolean;
  /** Path AFTER the slug segment, already encoded, leading slash included —
   *  e.g. an event's `/<itemId>` — kept on the redirect so a stale slug on a
   *  watch URL lands on the same recording, not on the detail page. */
  trailing?: string;
}) {
  const canonical = (opts.canonicalSlug ?? "").trim();
  if (!canonical) return;

  // The route param arrives URL-encoded for slugs that contain characters like
  // spaces or diacritics (the backend does produce slugs with spaces, e.g.
  // "minicity-otroški zaklad-..."). Decode it before comparing, otherwise the
  // encoded param ("...%20...") never equals the decoded canonical ("... ...")
  // and we redirect on every render — an infinite loop. Redirect TO an encoded
  // URL so the resulting path is valid (a raw space breaks RSC navigation).
  let decodedParam = opts.paramSlug;
  try {
    decodedParam = decodeURIComponent(opts.paramSlug);
  } catch {
    // Malformed percent-encoding — compare against the raw param as-is.
  }
  if (decodedParam !== canonical) {
    const prefix = opts.library ? "/library" : "";
    permanentRedirect(
      langPath(
        opts.lang,
        `${prefix}/${opts.type}/${opts.id}/${encodeURIComponent(canonical)}${opts.trailing ?? ""}`,
      ),
    );
  }
}

export function safeRedirect(path: string | null | undefined, fallback: string): string {
  if (!path) return fallback;
  // URL parsers strip control characters, potentially making a URL protocol-relative.
  if ([...path].some((c) => c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127)) return fallback;
  if (!path.startsWith("/")) return fallback;
  // Reject protocol-relative ("//host") AND backslash-tricked ("/\host")
  // targets: browsers normalize "\" to "/", so `Location: /\evil.com` resolves
  // as the cross-origin "//evil.com". Only the second char matters — a later
  // "//" (e.g. "/a//b") is a legitimate same-origin path.
  if (/^\/[/\\]/.test(path)) return fallback;
  return path;
}
